/** Smart Diagnostics — explains why SilentPoster may not be ready to send. */

import { motion } from "framer-motion";
import { AlertTriangle, ArrowLeft, CheckCircle2, ChevronRight, Info, ShieldCheck, Stethoscope } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button, Card } from "@/design";
import { useDiagnostics, type DiagnosticItem } from "@/api/growth";
import { cn } from "@/lib/cn";
import { haptic } from "@/lib/telegram";

const severityStyle = {
  ok: "text-success bg-success/15",
  info: "text-accent bg-accent/15",
  warning: "text-warning bg-warning/15",
  error: "text-danger bg-danger/15",
} as const;

export function DiagnosticsPage() {
  const navigate = useNavigate();
  const { data, isLoading, refetch, isFetching } = useDiagnostics();
  const next = data?.next_best_action;

  return (
    <div className="px-5 pt-4 pb-safe-nav space-y-5">
      <div className="flex items-center justify-between pt-1">
        <button
          onClick={() => {
            haptic.select();
            navigate(-1);
          }}
          className="size-10 rounded-full glass grid place-items-center hover:bg-white/[0.06] transition-colors"
          aria-label="Назад"
        >
          <ArrowLeft className="size-4" />
        </button>
        <div className="text-[15px] font-semibold tracking-tight">Диагностика</div>
        <button
          onClick={() => refetch()}
          className="size-10 rounded-full glass grid place-items-center hover:bg-white/[0.06] transition-colors"
          aria-label="Обновить"
        >
          <Stethoscope className={cn("size-4", isFetching && "animate-pulse text-accent")} />
        </button>
      </div>

      <Card padded={false} className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accent/20 via-transparent to-transparent" aria-hidden />
        <div className="relative p-6 space-y-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-medium text-text-muted">
            <ShieldCheck className="size-4 text-accent" />
            Smart Diagnostics
          </div>
          <div className="flex items-end justify-between gap-4">
            <div>
              <div className="text-[44px] leading-none font-semibold tabular-nums tracking-tight">
                {isLoading ? "…" : data?.score ?? 0}
                <span className="text-lg text-text-muted">/100</span>
              </div>
              <div className="text-sm text-text-muted mt-2 leading-relaxed">
                {data?.summary ?? "Проверяем аккаунты, чаты, шаблоны и состояние рассылки."}
              </div>
            </div>
          </div>
          {next?.action_url && (
            <Button fullWidth onClick={() => navigate(next.action_url!)}>
              {next.action_label ?? "Исправить"}
            </Button>
          )}
        </div>
      </Card>

      <div className="space-y-2">
        <div className="text-xs text-text-muted uppercase tracking-wider font-medium px-1">
          Что проверили
        </div>
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.04] overflow-hidden">
          {(data?.items ?? []).map((item, index) => (
            <DiagnosticRow key={item.code} item={item} index={index} onOpen={(url) => navigate(url)} />
          ))}
          {!data && (
            <div className="px-4 py-10 text-center text-xs text-text-muted">
              {isLoading ? "Запускаем проверку…" : "Нет данных"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DiagnosticRow({ item, index, onOpen }: { item: DiagnosticItem; index: number; onOpen: (url: string) => void }) {
  const Icon = item.severity === "ok" ? CheckCircle2 : item.severity === "info" ? Info : AlertTriangle;
  return (
    <motion.button
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.025, duration: 0.25 }}
      onClick={() => item.action_url && onOpen(item.action_url)}
      disabled={!item.action_url}
      className="w-full px-4 py-3.5 flex items-start gap-3 text-left disabled:cursor-default hover:bg-white/[0.03] transition-colors"
    >
      <div className={cn("size-9 rounded-xl grid place-items-center shrink-0", severityStyle[item.severity])}>
        <Icon className="size-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium">{item.title}</div>
        <div className="text-[11px] text-text-muted leading-relaxed mt-0.5">{item.description}</div>
      </div>
      {item.action_url && <ChevronRight className="size-4 text-text-dim mt-2 shrink-0" />}
    </motion.button>
  );
}
